import React, { useEffect, useState, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Api } from "../Api/Api";


const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [expenseDate, setExpenseDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [editingId, setEditingId] = useState(null);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("currentUser"));
    } catch {
      return null;
    }
  })();


  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [filterMonth, setFilterMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [filterDateFrom, setFilterDateFrom] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [filterDateTo, setFilterDateTo] = useState(
    new Date().toISOString().split("T")[0],
  );

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {

      const res = await fetch(`${Api}expenses/list.php`);
      const data = await res.json();
      setExpenses(data.expenses || []);
    } catch (err) {
      console.error("Failed to load expenses", err);
    }
  };

  const submitExpense = async () => {
    if (!title || !amount || !category || !expenseDate) {
      alert("Fill all fields");
      return;
    }


    const url = editingId
      ? `${Api}expenses/update.php`
      : `${Api}expenses/create.php`;

    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: editingId,
        title,
        amount,
        category,
        expense_date: expenseDate,
        created_by: currentUser?.username || "",
        created_by_id: currentUser?.userId || 0,
      }),
    });

    resetForm();
    loadExpenses();
  };

  const editExpense = (e) => {
    setTitle(e.title);
    setAmount(e.amount);
    setCategory(e.category);
    setExpenseDate(e.expense_date);
    setEditingId(e.id);
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;

    await fetch(`${Api}expenses/delete.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    loadExpenses();
  };

  const resetForm = () => {
    setTitle("");
    setAmount("");
    setCategory("");
    setExpenseDate(new Date().toISOString().split("T")[0]);
    setEditingId(null);
  };

  const filteredExpenses = useMemo(() => {
    if (filterType === "all") return expenses;
    if (filterType === "date")
      return expenses.filter((e) => e.expense_date === filterDate);
    if (filterType === "month")
      return expenses.filter((e) => e.expense_date.startsWith(filterMonth));
    if (filterType === "week") {
      const selected = new Date(filterDate);
      const start = new Date(selected);
      start.setDate(selected.getDate() - selected.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return expenses.filter((e) => {
        const d = new Date(e.expense_date);
        return d >= start && d <= end;
      });
    }
    if (filterType === "range") {
      return expenses.filter((e) => {
        return e.expense_date >= filterDateFrom && e.expense_date <= filterDateTo;
      });
    }
    return expenses;
  }, [expenses, filterType, filterDate, filterMonth, filterDateFrom, filterDateTo]);

  const totalAmount = filteredExpenses.reduce(
    (sum, e) => sum + Number(e.amount),
    0,
  );

  // --- PDF GENERATION LOGIC ---
  const downloadPDF = () => {
    const doc = new jsPDF();

    // 1. Title and Metadata
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text("OFFICIAL EXPENSE REPORT", 14, 20);

    doc.setFontSize(10);
    doc.text(`Report Period: ${filterType.toUpperCase()}`, 14, 28);
    doc.text(`Date Generated: ${new Date().toLocaleDateString()}`, 14, 33);
    doc.text(`Total Records: ${filteredExpenses.length}`, 14, 38);

    // 2. Table Columns (EXCLUDING ACTION)
    const tableColumn = [
      "#",
      "Description",
      "Category",
      "Date",
      "Amount (INR)",
    ];

    // 3. Table Rows
    const tableRows = filteredExpenses.map((expense, index) => [
      index + 1,
      expense.title,
      expense.category,
      expense.expense_date,
      Number(expense.amount).toFixed(2),
    ]);

    // 4. Using autoTable as a standalone function
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 45,
      theme: "grid",
      headStyles: {
        fillColor: [0, 0, 0], // Pure Black
        textColor: [255, 255, 255], // Pure White
        fontStyle: "bold",
        halign: "center",
      },
      styles: {
        fontSize: 9,
        textColor: [0, 0, 0], // Black text
        lineColor: [150, 150, 150], // Gray borders
      },
      columnStyles: {
        0: { cellWidth: 10 },
        4: { halign: "right", fontStyle: "bold" },
      },
      // Footer row for Auditor
      foot: [
        [
          "",
          "",
          "",
          "GRAND TOTAL",
          `INR ${totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        ],
      ],
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "right",
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
    });

    // Final Page Info
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" },
      );
    }

    doc.save(`Expense_Report_${filterDate}.pdf`);
  };


  const formatDateDMY = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};


  return (
    <div className="min-h-screen bg-gray-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            EXPENSE TRACKER
          </h1>
        </div>

        {/* INPUT FORM */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500">TITLE</label>
            <input
              placeholder="e.g. Office Supplies"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border px-3 py-2 rounded focus:ring-2 ring-indigo-500 outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500">AMOUNT</label>
            <input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="border px-3 py-2 rounded focus:ring-2 ring-indigo-500 outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500">CATEGORY</label>
            <input
              placeholder="e.g. Travel"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border px-3 py-2 rounded focus:ring-2 ring-indigo-500 outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500">DATE</label>
            <input
              type="date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              className="border px-3 py-2 rounded focus:ring-2 ring-indigo-500 outline-none"
            />
          </div>

          <div className="md:col-span-4 flex justify-end gap-3 mt-2">
            {editingId && (
              <button
                onClick={resetForm}
                className="px-5 py-2 text-sm font-bold text-gray-500 hover:text-gray-700"
              >
                CANCEL
              </button>
            )}
            <button
              onClick={submitExpense}
              className="px-8 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {editingId ? "UPDATE RECORD" : "SAVE EXPENSE"}
            </button>
          </div>
        </div>

        {/* FILTERS & PDF ACTION */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-wrap justify-between items-center gap-4">
          <div className="flex gap-4 items-center">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-gray-50 border border-gray-300 px-3 py-2 rounded font-bold text-xs uppercase"
            >
              <option value="all">All Records</option>
              <option value="date">Specific Date</option>
              <option value="range">Date Range</option>
              <option value="week">Current Week</option>
              <option value="month">Monthly View</option>
            </select>

            {["date", "week"].includes(filterType) && (
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="border px-3 py-2 rounded text-sm"
              />
            )}

            {filterType === "range" && (
              <>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="border px-3 py-2 rounded text-sm"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="border px-3 py-2 rounded text-sm"
                />
              </>
            )}

            {filterType === "month" && (
              <input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="border px-3 py-2 rounded text-sm"
              />
            )}
          </div>

          <button
            onClick={downloadPDF}
            className="flex items-center gap-2 px-6 py-2 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-all text-sm"
          >
            DOWNLOAD PDF (B&W)
          </button>
        </div>

        {/* TOTAL DISPLAY */}
        <div className="bg-white border-2 border-black p-4 rounded-xl flex justify-between items-center">
          <span className="text-gray-600 font-bold uppercase text-sm tracking-widest">
            Total Audited Amount
          </span>
          <span className="text-3xl font-black text-gray-900">
            ₹{totalAmount.toLocaleString("en-IN")}
          </span>
        </div>

        {/* DATA TABLE */}
        {/* DATA TABLE */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">
                  #
                </th>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">
                  Created By
                </th>

                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">
                  Title
                </th>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">
                  Category
                </th>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">
                  Date
                </th>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs text-right">
                  Amount
                </th>
                <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredExpenses.map((e, i) => (
                <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-gray-400">{i + 1}</td>
                  <td className="px-6 py-4 text-indigo-600 font-semibold text-sm">
                    {e.created_by || "—"}
                  </td>

                  <td className="px-6 py-4 font-semibold text-gray-800">
                    {e.title}
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">
                    {e.category}
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {" "}
                    {formatDateDMY(e.expense_date)}
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">
                    ₹{Number(e.amount).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-center space-x-4">
                    <button
                      onClick={() => editExpense(e)}
                      className="text-indigo-600 hover:text-indigo-900 font-bold text-xs uppercase"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteExpense(e.id)}
                      className="text-red-500 hover:text-red-700 font-bold text-xs uppercase"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filteredExpenses.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-12 text-gray-400 font-medium italic"
                  >
                    No expenses matching your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Expenses;